/**
 * Cart delivery alert (custom).
 *
 * Opens a modal the moment the cart starts mixing a pickup-only product with products that can be
 * delivered, because such an order can no longer be delivered at home.
 *
 * Whether the cart is mixed is decided server side, in the section's Liquid: the AJAX cart does not
 * expose product tags, so reading them in JavaScript is not an option. The element registers its
 * section in the theme's bundled sections, which makes every cart change come back with a freshly
 * rendered copy of the section. Only the state is read from that copy — the markup in the page is
 * left alone, so a modal that is already open never gets torn out from under the customer.
 */

class CartDeliveryAlert extends HTMLElement {
  #abortController;
  #wasMixed = false;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#wasMixed = this.dataset.cartIsMixed === 'true';

    const { signal } = this.#abortController;

    document.addEventListener('cart:prepare-bundled-sections', this.#onPrepareSections, { signal });
    document.addEventListener('cart:change', this.#onCartChange, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  get #sectionId() {
    return this.dataset.sectionId;
  }

  get #modal() {
    return this.querySelector('x-modal');
  }

  #onPrepareSections = (event) => {
    if (this.#sectionId) {
      event.detail.sections.push(this.#sectionId);
    }
  };

  #onCartChange = (event) => {
    const markup = event.detail?.cart?.sections?.[this.#sectionId];

    if (!markup) {
      return;
    }

    const rerendered = new DOMParser()
      .parseFromString(markup, 'text/html')
      .querySelector('cart-delivery-alert');

    if (!rerendered) {
      return;
    }

    const isMixed = rerendered.dataset.cartIsMixed === 'true';

    // Only the crossing matters: a cart that is already mixed must not warn again at every add.
    if (isMixed && !this.#wasMixed) {
      this.#modal?.show();
    }

    this.#wasMixed = isMixed;
  };
}

if (!window.customElements.get('cart-delivery-alert')) {
  window.customElements.define('cart-delivery-alert', CartDeliveryAlert);
}
